/**
 * Money helpers.
 *
 * Two integer units are used, never floating-point dollars:
 *
 *   - Cents:  what people type and pay. $4.99 is 499.
 *   - Micro:  1/10,000 of a cent. Tax and splits are computed in micro so that
 *             $7.53 split two ways stays exact ($3.765 each) and totals match the
 *             old Google Sheet, which carried fractions of a cent until the end.
 *
 * A JavaScript number holds integers exactly up to 2^53, which in micro is about
 * $9 billion, so overflow is not a concern for this app.
 */

export type Cents = number;
export type Micro = number;

export const MICRO_PER_CENT = 10_000;

/** Half a cent in micro: the tolerance used when deciding "paid" vs "still owes". */
export const HALF_CENT: Micro = MICRO_PER_CENT / 2;

/** Rounds to the nearest integer, with .5 going away from zero (like a receipt). */
export function roundHalfAway(x: number): number {
  return x < 0 ? -Math.round(-x) : Math.round(x);
}

export function centsToMicro(c: Cents): Micro {
  return c * MICRO_PER_CENT;
}

export function microToCents(m: Micro): Cents {
  return roundHalfAway(m / MICRO_PER_CENT);
}

/** "$1,234.56" or "-$2.80". */
export function formatCents(c: Cents): string {
  const sign = c < 0 ? "-" : "";
  const abs = Math.abs(c);
  // Plain grouping instead of toLocaleString, which builds a formatter on every call and was
  // the slowest part of working out a big ledger.
  const dollars = String(Math.floor(abs / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const rest = String(abs % 100).padStart(2, "0");
  return `${sign}$${dollars}.${rest}`;
}

export function formatMicro(m: Micro): string {
  return formatCents(microToCents(m));
}

/**
 * Parses money the way Google Sheets displays it:
 * "$1,234.56", "1234.5", "($2.80)" (negative), "-$3", "-" or "" (zero).
 * Returns null when the text isn't money at all.
 */
export function parseMoney(text: string): Cents | null {
  const t = text.trim();
  if (t === "" || t === "-") return 0;
  const negative = /^\(.*\)$/.test(t) || t.startsWith("-");
  const digits = t.replace(/[()$,\s-]/g, "");
  if (!/^\d+(\.\d{1,2})?$|^\.\d{1,2}$/.test(digits)) return null;
  const [whole = "0", frac = ""] = digits.split(".");
  const cents = Number(whole || "0") * 100 + Number(frac.padEnd(2, "0"));
  return negative ? -cents : cents;
}

/** Basis points: 7.75% is 775. Accepts "7.75%", "7.75", "27.75 %". */
export function parsePercentBp(text: string): number | null {
  const t = text.trim().replace(/%$/, "").trim();
  if (t === "" || t === "-") return 0;
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  const [whole = "0", frac = ""] = t.split(".");
  return Number(whole) * 100 + Number(frac.padEnd(2, "0"));
}

export function formatBp(bp: number): string {
  return `${(bp / 100).toFixed(2)}%`;
}

/**
 * Splits `total` into integer parts proportional to `weights`, using the
 * largest-remainder method so the parts always add up to `total` exactly.
 * Ties go to the earlier entry, so the result is stable.
 */
export function splitByWeights(total: number, weights: readonly number[]): number[] {
  const sum = weights.reduce((a, w) => a + w, 0);
  if (weights.length === 0 || sum <= 0) return weights.map(() => 0);
  const sign = total < 0 ? -1 : 1;
  const abs = Math.abs(total);
  const exact = weights.map((w) => (abs * w) / sum);
  const parts = exact.map((x) => Math.floor(x));
  let left = abs - parts.reduce((a, p) => a + p, 0);
  const order = exact
    .map((x, i) => ({ i, frac: x - Math.floor(x) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i);
  for (const { i } of order) {
    if (left <= 0) break;
    parts[i] = (parts[i] ?? 0) + 1;
    left -= 1;
  }
  return parts.map((p) => p * sign);
}
